<script setup lang="ts">
import { computed, type Ref } from 'vue';
import { useLayout } from '@/composables/useLayout';
import type Carousel from '@/components/widgets/Carousel.vue';

// Tipo antigo (Swiper.vue)
type SwiperControls = {
  scrollNext: () => void;
  scrollPrev: () => void;
  currentScrollLeft: Ref<number>;
  currentScrollWidth: Ref<number>;
  currentClientWidth: Ref<number>;
};

// Suporte para ambos os tipos
type SwiperRefType = InstanceType<typeof Carousel> | SwiperControls | null;

const props = withDefaults(
  defineProps<{
    iconPath?: string;
    title?: string;
    seeMoreUrl?: string;
    showSeeMore?: boolean;
    providertitle?: {
      background?: string;
      borderRadius?: string;
      marginBottom?: string;
      paddingBottom?: any;
      paddingRight?: string;
    };
    /**
     * Quando true, esconde as setas de navegação mesmo que swiperRef exista.
     * Prop ausente = false (Vue boolean casting) = auto-detecta via swiperRef.
     */
    hideNavigation?: boolean;
    swiperRef?: SwiperRefType;
    scrollDistance?: number;
    prevIcon?: string;
    nextIcon?: string;
    /**
     * 'bordered' = "VER MAIS" com borda ao redor (padrão para seções sem setas)
     * 'arrow-bordered' = "VER MAIS" sem borda + seta com borda (para seções numeric)
     */
    seeMoreVariant?: 'bordered' | 'arrow-bordered';
    seeMoreText?: string;
    seeMoreTextMobile?: string;
  }>(),
  {
    showSeeMore: true,
    seeMoreVariant: 'bordered',
    seeMoreText: 'VER MAIS',
    seeMoreTextMobile: 'Ver todos',
    prevIcon: 'perfil-oficial/back',
    nextIcon: 'perfil-oficial/next',
    iconPath: undefined,
    title: undefined,
    seeMoreUrl: undefined,
    providertitle: undefined,
    swiperRef: undefined,
    scrollDistance: undefined,
  },
);

const { isMobile } = useLayout();

const isSeeMoreButtonVisible = computed<boolean>(() => {
  return props.showSeeMore && !!props.seeMoreUrl;
});

// Verifica se é o tipo novo (Carousel)
const isCarouselType = (
  ref: SwiperRefType,
): ref is InstanceType<typeof Carousel> => {
  return ref !== null && 'scrollToLeft' in ref && 'scrollToRight' in ref;
};

// Verifica se é o tipo antigo (SwiperControls)
const isSwiperControlsType = (ref: SwiperRefType): ref is SwiperControls => {
  return (
    ref !== null &&
    'scrollNext' in ref &&
    'scrollPrev' in ref &&
    'currentScrollLeft' in ref
  );
};

const effectiveShowNavigation = computed<boolean>(() => {
  if (props.hideNavigation) return false;

  return !!props.swiperRef;
});

const isLeftButtonDisabled = computed(() => {
  if (!props.swiperRef) return true;

  // Tipo novo (Carousel)
  if (isCarouselType(props.swiperRef)) {
    return props.swiperRef.isNavigationLeftButtonDisabled ?? true;
  }

  // Tipo antigo (SwiperControls)
  if (isSwiperControlsType(props.swiperRef)) {
    const scrollLeft = props.swiperRef.currentScrollLeft?.value ?? 0;

    return scrollLeft <= 0;
  }

  return true;
});

const isRightButtonDisabled = computed(() => {
  if (!props.swiperRef) return true;

  // Tipo novo (Carousel)
  if (isCarouselType(props.swiperRef)) {
    return props.swiperRef.isNavigationRightButtonDisabled ?? true;
  }

  // Tipo antigo (SwiperControls)
  if (isSwiperControlsType(props.swiperRef)) {
    const { currentScrollLeft, currentClientWidth, currentScrollWidth } =
      props.swiperRef;

    const scrollLeft = currentScrollLeft?.value ?? 0;
    const clientWidth = currentClientWidth?.value ?? 0;
    const scrollWidth = currentScrollWidth?.value ?? 0;

    // Se não há overflow, desabilita o botão direito
    if (scrollWidth <= clientWidth) return true;

    return scrollLeft + clientWidth >= scrollWidth;
  }

  return true;
});

const handleScrollLeft = () => {
  if (!props.swiperRef) return;

  if (isCarouselType(props.swiperRef)) {
    props.swiperRef.scrollToLeft(props.scrollDistance ?? 300);
  } else if (isSwiperControlsType(props.swiperRef)) {
    props.swiperRef.scrollPrev();
  }
};

const handleScrollRight = () => {
  if (!props.swiperRef) return;

  if (isCarouselType(props.swiperRef)) {
    props.swiperRef.scrollToRight(props.scrollDistance ?? 300);
  } else if (isSwiperControlsType(props.swiperRef)) {
    props.swiperRef.scrollNext();
  }
};
</script>

<template>
  <div
    :class="$style.header"
    :draggable="false"
  >
    <div :class="$style.titleContainer">
      <div
        v-if="props.iconPath"
        :class="$style.iconWrapper"
      >
        <NuxtIcon
          class="text-[30px] w-full h-full"
          :filled="true"
          :name="props.iconPath"
        />
      </div>
      <p
        v-if="props.title"
        :class="$style.titleText"
        >{{ props.title }}</p
      >
    </div>

    <!-- VER MAIS: variante 'arrow-bordered' (numeric) - texto sem borda + seta com borda -->
    <NuxtLink
      v-if="isSeeMoreButtonVisible && props.seeMoreVariant === 'arrow-bordered'"
      class="flex items-center justify-center ml-auto gap-[5px]"
      :draggable="false"
      no-prefetch
      :to="props.seeMoreUrl!"
    >
      <span class="text-[11px] md:px-2.5 text-white font-bold leading-4">{{
        props.seeMoreText
      }}</span>
      <span :class="$style.seeMoreArrowBordered">
        <NuxtIcon
          class="text-[10px] text-white"
          :filled="true"
          name="arrows/chevron_right"
        />
      </span>
    </NuxtLink>

    <!-- Desktop com navegação: [VER MAIS] [←] [→] todos com borda, agrupados -->
    <div
      v-else-if="isSeeMoreButtonVisible && effectiveShowNavigation && !isMobile"
      :class="$style.actions"
    >
      <slot name="actions" />
      <NuxtLink
        :class="$style.seeMoreBordered"
        :draggable="false"
        no-prefetch
        :to="props.seeMoreUrl!"
      >
        <span
          class="text-[11px] text-white font-bold leading-4 tracking-[0.6px]"
          >{{ props.seeMoreText }}</span
        >
      </NuxtLink>
      <PerfilOficialNavArrowButton
        direction="left"
        :disabled="isLeftButtonDisabled"
        @click="handleScrollLeft"
      />
      <PerfilOficialNavArrowButton
        direction="right"
        :disabled="isRightButtonDisabled"
        @click="handleScrollRight"
      />
    </div>

    <!-- Desktop sem navegação ou mobile: VER MAIS simples -->
    <template v-else-if="isSeeMoreButtonVisible">
      <!-- Desktop sem navegação: VER MAIS com borda -->
      <NuxtLink
        v-if="!isMobile"
        :class="[
          'flex items-center justify-center ml-auto gap-1 px-2.5',
          $style.seeMoreBordered,
        ]"
        :draggable="false"
        no-prefetch
        :to="props.seeMoreUrl!"
      >
        <span
          class="text-[11px] text-white font-bold leading-4 tracking-[0.6px]"
          >{{ props.seeMoreText }}</span
        >
      </NuxtLink>
      <!-- Mobile: VER MAIS + seta simples, sem borda -->
      <NuxtLink
        v-else
        class="flex items-center justify-center ml-auto gap-1 pl-2.5"
        :draggable="false"
        no-prefetch
        :to="props.seeMoreUrl!"
      >
        <span class="text-[14px] text-white font-medium leading-5">{{
          props.seeMoreTextMobile
        }}</span>
        <NuxtIcon
          class="text-[14px] text-white"
          name="arrows/chevron_right"
        />
      </NuxtLink>
    </template>

    <!-- Desktop com navegação mas sem see-more: apenas ← → -->
    <div
      v-else-if="effectiveShowNavigation && !isMobile"
      :class="$style.actions"
    >
      <slot name="actions" />
      <PerfilOficialNavArrowButton
        direction="left"
        :disabled="isLeftButtonDisabled"
        @click="handleScrollLeft"
      />
      <PerfilOficialNavArrowButton
        direction="right"
        :disabled="isRightButtonDisabled"
        @click="handleScrollRight"
      />
    </div>
  </div>
</template>

<style module lang="postcss">
.header {
  @apply flex justify-between items-center rounded-md mb-2.5 lg:mb-4;
  user-select: none;
}

.titleContainer {
  @apply flex items-center justify-center gap-[2px];
}

.iconWrapper {
  @apply flex-shrink-0 flex items-center justify-center;
  width: 30px;
  height: 30px;
}

.titleText {
  @apply text-white text-[16px] font-bold leading-6 whitespace-nowrap;
}

.actions {
  @apply flex items-center gap-[5px] ml-auto;
}

.seeMoreBordered {
  @apply flex items-center justify-center;
  border-radius: 5.11px;
  border: 1.022px solid rgba(255, 255, 255, 0.1);
  padding: 2px 7px;
}

.seeMoreArrowBordered {
  @apply flex items-center justify-center;
  border-radius: 5.11px;
  border: 1.022px solid rgba(255, 255, 255, 0.1);
  padding: 4px 7px;
}
</style>
